/**
 * Tag-registry taxonomy and wire DTOs shared by registration callers and the
 * proxy `POST /api/v1/ontology/tags/batch` surface.
 */

import type { DbDatapointInput } from '@atbash-labs/ontology-db'

export const TAG_VENDORS = ['ignition', 'aveva', 'direct'] as const
export type TagVendor = (typeof TAG_VENDORS)[number]

export const TAG_PROTOCOLS = [
  'opc',
  'webdev',
  'gateway_link',
  'mtconnect',
  'db',
  'mqtt',
  'pi',
  'canary'
] as const
export type TagProtocol = (typeof TAG_PROTOCOLS)[number]

/** Protocols an Ignition ingest can use to register live tag reads. */
export type IgnitionTagRegistrationProtocol = Extract<
  TagProtocol,
  'opc' | 'webdev' | 'gateway_link'
>

export const TAG_SOURCE_TYPES = [
  'ignition',
  'opcua',
  'opcda',
  'mtconnect',
  'database',
  'pi',
  'canary'
] as const
export type TagSourceType = (typeof TAG_SOURCE_TYPES)[number]
/** Decode fallback — not a storable registry value. */
export type TagSourceTypeName = TagSourceType | 'unspecified'

export const TAG_SCRAPE_MODES = ['poll', 'subscribe', 'stream'] as const
export type TagScrapeMode = (typeof TAG_SCRAPE_MODES)[number]

const SOURCE_TYPE_SET: ReadonlySet<string> = new Set(TAG_SOURCE_TYPES)
const SCRAPE_MODE_SET: ReadonlySet<string> = new Set(TAG_SCRAPE_MODES)

/** Exact-match decode; alias normalization is the proxy's job on write. */
export function tagSourceTypeFromWire(value: unknown): TagSourceTypeName {
  return typeof value === 'string' && SOURCE_TYPE_SET.has(value)
    ? (value as TagSourceType)
    : 'unspecified'
}

/** Exact-match decode; unknown/absent collapses to poll (registry default). */
export function tagScrapeModeFromWire(value: unknown): TagScrapeMode {
  return typeof value === 'string' && SCRAPE_MODE_SET.has(value) ? (value as TagScrapeMode) : 'poll'
}

export interface TagBatchOpcConfig {
  endpoint_url?: string
  node_id?: string
  browse_path?: string
  vendor_path?: string
  translator_version?: number
  relay_opc_server?: string
  relay_node_id?: string
  relay_gateway_url?: string
  relay_webdev_project?: string
}

export interface TagBatchWebDevConfig {
  gateway_url?: string
  project?: string
  read_route?: 'tags' | 'opc-relay'
  tag_path?: string
  opc_server?: string
  opc_node_id?: string
}

export interface TagBatchMTConnectConfig {
  agent_url?: string
  device?: string
  data_item?: string
}

export interface TagBatchPIConfig {
  web_id: string
  point_name?: string
  dataserver_web_id?: string
}

/**
 * Canary Views address for the `tag_canary_configs` sidecar. `tag_name` is the
 * fully-qualified Canary name verbatim (names may contain `.` and `/`, so it
 * is never derived from the path); view/dataset are optional display context.
 */
export interface TagBatchCanaryConfig {
  tag_name: string
  view?: string
  dataset?: string
}

export type TagBatchCanaryAuthMode = 'anonymous' | 'api_token' | 'user'

/**
 * Top-level `canary_connection` block of `POST /api/v1/ontology/tags/batch`,
 * persisted to `canary_endpoints`. Exactly the credentials `auth_mode` needs
 * may be set; omitting `auth_mode` is a posture-only refresh that must carry
 * no credentials. Only the first chunk of a registration carries secrets.
 */
export interface TagBatchCanaryConnection {
  base_url: string
  auth_mode?: TagBatchCanaryAuthMode
  api_token?: string
  username?: string
  password?: string
  skip_tls_verify?: boolean
}

/** One item of `POST /api/v1/ontology/tags/batch`. Mirrors the proxy `batchTagItem`. */
export interface TagBatchItem {
  tag_path: string
  opc_node_id?: string
  node_id?: string
  vendor?: TagVendor
  protocol?: TagProtocol
  source_url: string
  source_type: TagSourceType
  scrape_mode?: TagScrapeMode
  display_label?: string
  frequency_ms?: number
  /** Only set when the caller explicitly chose enablement; omit to preserve. */
  enabled?: boolean
  opc?: TagBatchOpcConfig
  webdev?: TagBatchWebDevConfig
  mtconnect?: TagBatchMTConnectConfig
  /** PI Web API stream address. Required for protocol/source_type `pi`. */
  pi?: TagBatchPIConfig
  /** Canary Views tag address. Required for protocol/source_type `canary`. */
  canary?: TagBatchCanaryConfig
  /** Proxy `db` block — same shape as `@atbash-labs/ontology-db` `DbDatapointInput`. */
  db?: DbDatapointInput
}

/** Body of `POST /api/v1/ontology/tags/batch` as the registration callers post it. */
export interface TagBatchRequestBody {
  tags: TagBatchItem[]
  canary_connection?: TagBatchCanaryConnection
  preserve_existing_enabled?: boolean
  preserve_existing_acquisition?: boolean
}

export interface TranslationError {
  ok: false
  reason: string
  tagPath?: string
}

export interface TranslationSuccess {
  ok: true
  item: TagBatchItem
}

export type TranslationResult = TranslationSuccess | TranslationError

export interface VendorTranslator<TInput> {
  translate(input: TInput): TranslationResult
}
